import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import RateModal from './heavyWeightRates/RateModal'

// All 8 known zones with country hints
const ALL_ZONES: { key: string; label: string; countries: string }[] = [
  { key: 'zone1', label: 'Zone 1', countries: 'UK, Ireland, Guernsey, Jersey' },
  {
    key: 'zone2',
    label: 'Zone 2',
    countries: 'Ghana, Cameroon, Senegal, West Africa',
  },
  { key: 'zone3', label: 'Zone 3', countries: 'USA, Canada, Mexico' },
  {
    key: 'zone4',
    label: 'Zone 4',
    countries: 'Germany, France, Italy, Spain, Europe',
  },
  {
    key: 'zone5',
    label: 'Zone 5',
    countries: 'Kenya, South Africa, Ethiopia, East/Southern Africa',
  },
  {
    key: 'zone6',
    label: 'Zone 6',
    countries: 'UAE, Saudi Arabia, Qatar, Middle East',
  },
  {
    key: 'zone7',
    label: 'Zone 7',
    countries: 'China, India, Japan, Singapore, Asia-Pacific',
  },
  {
    key: 'zone8',
    label: 'Zone 8',
    countries: 'Brazil, Chile, Argentina, Australia, Americas & Pacific',
  },
]

interface RateEntry {
  id: string
  zone: string
  user: number
  aggregator: number
  superAggregator: number
  special: number
  trustedUser: number
  createdAt: string
  updatedAt: string
}

function HeavyWeightRates() {
  const { notify } = useGlobalContext()
  const [rates, setRates] = useState<RateEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedRate, setSelectedRate] = useState<RateEntry | null>(null)
  const [prefilledZone, setPrefilledZone] = useState<string | undefined>(
    undefined,
  )

  function fetchRates() {
    setLoading(true)
    get('heavy-weight-rates/fetch')
      .then((result: any) => {
        if (result.status === 200) {
          // API returns { data: [...] }
          setRates(result.data ?? [])
        } else {
          notify('error', result.message ?? 'Failed to load rates')
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchRates()
  }, [])

  function openAddModal(zoneKey: string) {
    setSelectedRate(null)
    setPrefilledZone(zoneKey)
    setShowModal(true)
  }

  function openEditModal(rate: RateEntry) {
    setSelectedRate(rate)
    setPrefilledZone(undefined)
    setShowModal(true)
  }

  // Map fetched rates by zone key for O(1) lookups
  const rateMap = new Map<string, RateEntry>(rates.map((r) => [r.zone, r]))

  const allConfigured = ALL_ZONES.every((z) => rateMap.has(z.key))

  return (
    <div className='home'>
      {/* Page header */}
      <div className='home_tag flex justify-between'>
        <span />
        <div className='flex buttons'>
          <button
            onClick={() => {
              setSelectedRate(null)
              setPrefilledZone(undefined)
              setShowModal(true)
            }}
          >
            <Icon icon='mdi:plus' style={{ marginRight: 4 }} />
            Add Zone Rate
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div
        style={{
          margin: '0 0 18px 0',
          padding: '12px 18px',
          background: 'var(--accent-light, #f0f7ff)',
          borderLeft: '4px solid var(--primary, #2563eb)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          fontSize: '0.87rem',
          color: 'var(--text, #334155)',
          lineHeight: 1.6,
        }}
      >
        <Icon
          icon='mdi:information-outline'
          style={{
            fontSize: 20,
            color: 'var(--primary, #2563eb)',
            marginTop: 1,
            flexShrink: 0,
          }}
        />
        <div>
          <strong>Heavy Weight Rates (≥ 50 kg)</strong> — These per-kg prices
          replace DHL pricing for non-document international shipments weighing
          50 kg or more.{' '}
          <span style={{ opacity: 0.75 }}>
            Formula: <code>Math.ceil(weight) × rate[role]</code>. No VAT
            applied.
          </span>
          {!allConfigured && (
            <span
              style={{
                display: 'block',
                marginTop: 4,
                color: '#b45309',
                fontWeight: 600,
              }}
            >
              ⚠️ Some zones are missing rates. Customers booking to those zones
              will receive a pricing error until they are configured.
            </span>
          )}
        </div>
      </div>

      {/* Main table */}
      <div className='home_tables'>
        <div className='left'>
          {loading ? (
            <FormLoader position='center' />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>Countries</th>
                  <th>User (₦/kg)</th>
                  <th>Aggregator (₦/kg)</th>
                  <th>Super-Agg (₦/kg)</th>
                  <th>Special (₦/kg)</th>
                  <th>Trusted User (₦/kg)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {ALL_ZONES.map((zone) => {
                  const entry = rateMap.get(zone.key)
                  const hasRate = !!entry

                  return (
                    <tr key={zone.key}>
                      <td>
                        <strong>{zone.label}</strong>
                      </td>
                      <td style={{ fontSize: '0.82rem', opacity: 0.75 }}>
                        {zone.countries}
                      </td>

                      {hasRate ? (
                        <>
                          <td>{entry!.user.toLocaleString()}</td>
                          <td>{entry!.aggregator.toLocaleString()}</td>
                          <td>{entry!.superAggregator.toLocaleString()}</td>
                          <td>{entry!.special.toLocaleString()}</td>
                          <td>{entry!.trustedUser.toLocaleString()}</td>
                          <td className='icons'>
                            <button
                              className='table-btn edit-btn'
                              onClick={() => openEditModal(entry!)}
                            >
                              <Icon icon='fa-solid:edit' />
                              Edit
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className='count'>—</td>
                          <td className='count'>—</td>
                          <td className='count'>—</td>
                          <td className='count'>—</td>
                          <td className='count'>—</td>
                          <td className='icons'>
                            <button
                              className='table-btn add-btn'
                              onClick={() => openAddModal(zone.key)}
                            >
                              <Icon icon='mdi:plus-circle-outline' />
                              Add
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <RateModal
          setShowModal={setShowModal}
          fetchRates={fetchRates}
          selectedRate={selectedRate}
          prefilledZone={prefilledZone}
        />
      )}
    </div>
  )
}

export default HeavyWeightRates
